import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { MatMenuTrigger } from '@angular/material/menu';
import { PageEvent } from '@angular/material/paginator';
import { Store } from '@ngrx/store';
import isEmpty from 'lodash-es/isEmpty';
import isEqual from 'lodash-es/isEqual';
import isNil from 'lodash-es/isNil';
import trim from 'lodash-es/trim';
import { SubscriptionComponent } from '@liebherr/old-sage-ui';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, first, map, startWith, withLatestFrom } from 'rxjs/operators';
import { AppState } from '../../../app.models';
import * as fromDevices from '../../../devices';
import { RouterSorting } from './../../../api/core/models/router-sorting';
import { RouterDto } from '../../../api/core';
import { registerApiCall } from '../../../utils';

@Component({
    selector: 'liebherr-device-search',
    templateUrl: 'device-search.component.html',
    styleUrls: ['device-search.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeviceSearchComponent extends SubscriptionComponent implements AfterViewInit {
    @Input() parentControl?: UntypedFormControl | AbstractControl;
    @Input() label?: string;
    columns = ['id', 'serialNumber'];

    filterStringCtrl = this.fb.control(undefined);

    SEARCH_OPTIONS = fromDevices.SearchOptions;

    selectedSearchOption$ = new BehaviorSubject<fromDevices.SearchOptions>(fromDevices.SearchOptions.SERIALNUMBER);
    searchOptions = Object.values(fromDevices.SearchOptions);

    filterString$ = this.filterStringCtrl.valueChanges.pipe(
        distinctUntilChanged(isEqual),
        map((change) => (!isEmpty(trim(change)) ? trim(change) : undefined)),
        startWith(undefined)
    );
    initialDevice$ = new BehaviorSubject<RouterDto | undefined>(undefined);
    page$ = new BehaviorSubject<number>(0);
    availableDevices$: Observable<RouterDto[]> = this.store.select(fromDevices.selectDevices);
    availableDevicesCount$ = this.store.select(fromDevices.selectCount);
    loading$ = this.store.select(fromDevices.selectLoading);

    currentDevice$ = combineLatest([this.availableDevices$, this.filterString$, this.selectedSearchOption$, this.initialDevice$]).pipe(
        map(([devices, filterString, searchOption, initialDevice]) =>
            this.getCurrentDevice(devices, searchOption, filterString, initialDevice)
        )
    );
    ctrl = this.fb.control(undefined);

    matchErrKey = 'noRouterMatch';

    constructor(private store: Store<AppState>, private fb: UntypedFormBuilder, private cd: ChangeDetectorRef) {
        super();
    }

    ngAfterViewInit() {
        if (this.parentControl) {
            this.filterStringCtrl.patchValue(this.parentControl?.value);
            this.loadInitialDevice(this.parentControl?.value);
        }
        // update parent control with current device
        this.sub.add(
            this.currentDevice$
                .pipe(withLatestFrom(this.filterString$, this.initialDevice$))
                .subscribe(([device, filterString, initialDevice]) => {
                    this.parentControl?.patchValue(device?.id);

                    let errors = this.parentControl?.errors ? { ...this.parentControl?.errors } : {};
                    // valid options are device?.id or undefined -> prevent random string as value
                    // set error when a filterString is set and the device is undefined
                    if (isNil(device) && !isNil(filterString)) {
                        // set error
                        errors = { ...errors, [this.matchErrKey]: true };
                    } else {
                        // clear errors
                        if (this.parentControl?.hasError(this.matchErrKey)) {
                            delete errors[this.matchErrKey];
                        }
                    }
                    this.parentControl?.setErrors(!isEmpty(errors) ? errors : null);

                    if (this.parentControl?.value === initialDevice?.id || this.parentControl?.value === initialDevice?.serialNumber) {
                        // mark parentControl pristine when its value is the same as initial device
                        this.parentControl?.markAsPristine();
                    } else {
                        // mark parentControl as dirty when its value has changed from initial value
                        this.parentControl?.markAsDirty();
                    }
                })
        );

        // load available devices on filter or page change
        this.sub.add(
            combineLatest([this.filterString$, this.page$])
                .pipe(withLatestFrom(this.selectedSearchOption$))
                .subscribe(([[filterString, page], searchOption]) => {
                    this.loadAvailableDevices(searchOption, filterString, page);
                })
        );
    }

    onFocus(trigger?: MatMenuTrigger) {
        this.openMenu(trigger);
        this.loadAvailableDevices(this.selectedSearchOption$.getValue(), this.filterStringCtrl?.value, this.page$.getValue());
    }

    openMenu(trigger?: MatMenuTrigger) {
        if (trigger && !trigger.menuOpen) {
            trigger.openMenu();
        }
    }

    onPage(event: PageEvent) {
        this.page$.next(event.pageIndex);
    }

    onSelection(device: RouterDto, trigger?: MatMenuTrigger) {
        const searchOption = this.selectedSearchOption$.getValue();
        this.filterStringCtrl.patchValue(searchOption === fromDevices.SearchOptions.ROUTERID ? device.id : device.serialNumber);
        this.filterStringCtrl.markAsDirty();
        this.parentControl?.markAsDirty();
        this.page$.next(0); // important to get match in getCurrentDevice()
        if (trigger) {
            trigger.closeMenu();
        }
    }
    onChangeSearchOptions(option: fromDevices.SearchOptions, currentDevice?: RouterDto) {
        this.selectedSearchOption$.next(option);

        if (option === fromDevices.SearchOptions.ROUTERID) {
            this.filterStringCtrl.patchValue(currentDevice?.id);
        } else {
            this.filterStringCtrl.patchValue(currentDevice?.serialNumber);
        }
    }

    private loadInitialDevice(routerId?: string) {
        if (routerId) {
            registerApiCall(
                () => this.store.dispatch(fromDevices.loadSingleDevice({ routerId })),
                fromDevices.DeviceActionTypes.LOADED_SINGLE_DEVICE
            )
                .pipe(first())
                .subscribe(
                    (a) => {
                        this.initialDevice$.next(a.device);
                        this.filterStringCtrl.patchValue(
                            this.selectedSearchOption$.getValue() === fromDevices.SearchOptions.ROUTERID
                                ? a.device.id
                                : a.device.serialNumber
                        );
                    },
                    () => {
                        this.initialDevice$.next(undefined);
                        this.filterStringCtrl.patchValue(undefined);
                    }
                );
        } else {
            this.initialDevice$.next(undefined);
            this.filterStringCtrl.patchValue(undefined);
        }
    }

    private loadAvailableDevices(searchOption: fromDevices.SearchOptions, filterString?: string, page?: number) {
        this.store.dispatch(
            fromDevices.loadDevices({
                params: {
                    routerId: searchOption === fromDevices.SearchOptions.ROUTERID ? filterString : undefined,
                    serialNumber: searchOption === fromDevices.SearchOptions.SERIALNUMBER ? filterString : undefined,
                    page: page || 0,
                    rowLimit: 5,
                    sortAsc: true,
                    sortColumn: searchOption === fromDevices.SearchOptions.ROUTERID ? RouterSorting.DeviceId : RouterSorting.SerialNumber,
                    hasSimcard: false,
                },
            })
        );
    }

    private getCurrentDevice(
        avaliableDevices: RouterDto[],
        searchOption: fromDevices.SearchOptions,
        filterString?: string,
        initialDevice?: RouterDto
    ) {
        // check for match in availabled devices
        const availableMatch = avaliableDevices.find((d) =>
            searchOption === fromDevices.SearchOptions.ROUTERID ? d.id === filterString : d.serialNumber === filterString
        );
        // check if filterstring matches with initial device
        const initialMatch = initialDevice
            ? searchOption === fromDevices.SearchOptions.ROUTERID
                ? initialDevice?.id === filterString
                : initialDevice?.serialNumber === filterString
            : false;
        // return initial device or available match
        return initialMatch ? initialDevice : availableMatch;
    }
}
